import { useEffect, useMemo, useRef } from 'react';
import { ComboBox, TextInputSkeleton } from '@carbon/react';
import { useAtom } from 'jotai';
import { startCase } from 'lodash-es';
import { DcaScenariosResponse } from 'types/api-responses';
import { dcaScenariosAtom } from './atoms';
import { defaultDcaScenario } from './utils';
import style from './dca-scenario-filter.module.scss';

const DCA_SCENARIOS_HREF = '/queries/reservoir/dca_scenarios';

/** Display names for scenarios whose stored name is not what we show. */
const DISPLAY_NAMES: Record<string, string> = {
  'pdp default': 'PDP Default',
  'MLLE': 'MLLE',
};

const displayName = (scenario: DcaScenariosResponse) =>
  DISPLAY_NAMES[scenario.name] ?? startCase(scenario.name);

export interface DcaScenarioFilterProps {
  value: DcaScenariosResponse | null;
  onChange: (scenario: DcaScenariosResponse | null) => void;
}

/**
 * Selects the DCA Scenario used to filter the Insights PDP forecast versions
 * next to it.
 *
 * PDP Default is selected once the scenarios arrive, or the first scenario if
 * there is no PDP Default. Clearing the box removes the filter, and the
 * version list then shows every forecast the well has.
 *
 * @param {DcaScenarioFilterProps} props - The selected scenario and a way to change it.
 * @return {JSX.Element} The combo box.
 */
export const DcaScenarioFilter = ({
  value,
  onChange,
}: DcaScenarioFilterProps) => {
  const [scenarios, fetchScenarios] = useAtom(dcaScenariosAtom);
  // The selection below is made once, when the scenarios arrive. Without this
  // a later fetch would overwrite whatever the user has since chosen.
  const preselectedRef = useRef(false);

  useEffect(() => {
    fetchScenarios({
      url: DCA_SCENARIOS_HREF,
      type: 'POST',
      data: {
        query: {
          page: 1,
          pageSize: 50,
          sort_attribute: 'created_at',
          sort_direction: 'DESC',
        },
      },
      onSuccess: (data) => {
        if (preselectedRef.current) return;
        preselectedRef.current = true;

        const scenario = defaultDcaScenario(data.results);
        if (scenario) onChange(scenario);
      },
    });
    // `onChange` is the caller's setter and is only read inside onSuccess, so
    // it is left out to keep this to the one fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchScenarios]);

  const options = useMemo(() => {
    const result = scenarios.result;
    if (!result || result.type !== 'success') return [];

    return result.data.results;
  }, [scenarios.result]);

  if (scenarios.loading) {
    return (
      <div className={style.skeleton}>
        <TextInputSkeleton hideLabel data-testid="dca-scenario-skeleton" />
      </div>
    );
  }

  return (
    <div className={style.filter} data-testid="dca-scenario-filter">
      <ComboBox<DcaScenariosResponse>
        id="well-analysis-dca-scenario"
        titleText="DCA Scenarios"
        size="lg"
        items={options}
        itemToString={(scenario) => (scenario ? displayName(scenario) : '')}
        placeholder={
          options.length === 0 ? 'No scenarios available' : 'Select a scenario'
        }
        selectedItem={value}
        // Clearing the box selects no scenario, which removes the filter.
        onChange={({ selectedItem }) => onChange(selectedItem ?? null)}
        shouldFilterItem={({ item, inputValue }) =>
          displayName(item)
            .toLowerCase()
            .includes((inputValue ?? '').toLowerCase())
        }
      />
    </div>
  );
};
